import React from 'react';
import { Box, Card, CardContent, Typography, Chip, CircularProgress } from '@mui/material';

export interface OrganizationStat {
  type: string;
  kpiScore: number;
  userCount: number;
  taskCount: number;
  completedTaskCount: number;
}

interface OrganizationStatsProps {
  organizations: OrganizationStat[];
  loading?: boolean;
}

export const OrganizationStats: React.FC<OrganizationStatsProps> = ({ 
  organizations, 
  loading = false 
}) => {
  const formatKpiScore = (score: number): string => {
    if (score > 0) {
      return `+${score} days`;
    } else if (score < 0) {
      return `${score} days`;
    } else {
      return '0 days';
    }
  };
  
  // NOTE: KPI scores are calculated by the backend using duration-based calculation (business days).
  // Negative KPI = reclaimed time (ahead of schedule) = GREEN (good)
  // Positive KPI = overdue/delayed = RED (bad)
  // Zero = on time = default (gray)

  const getKpiColor = (score: number): 'error' | 'success' | 'default' => {
    if (score < 0) return 'success'; // Green for reclaimed time (negative = ahead of schedule)
    if (score > 0) return 'error'; // Red for overdue (positive = delayed)
    return 'default'; // Gray for on time
  };

  if (loading) {
    return (
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {[1, 2, 3].map((i) => (
          <Card key={i} sx={{ minWidth: 200, flex: '1 1 auto' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="h6" component="div">
                  Loading...
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  }

  if (!organizations || organizations.length === 0) {
    // Show a placeholder when execution stats are not yet available
    return (
      <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Organization stats will appear here once project execution data is available.
        </Typography>
      </Box>
    );
  }

  // Calculate project total (aggregate of all organization KPIs)
  const projectTotalKpi = organizations.reduce((sum, org) => sum + org.kpiScore, 0);
  const projectTotalUsers = organizations.reduce((sum, org) => sum + org.userCount, 0);
  const projectTotalCompletedTasks = organizations.reduce((sum, org) => sum + org.completedTaskCount, 0);

  return (
    <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      {/* Project Total Card - shown first */}
      <Card sx={{ minWidth: 200, flex: '1 1 auto', border: '2px solid', borderColor: 'primary.main' }}>
        <CardContent>
          <Typography variant="h6" component="div" gutterBottom sx={{ fontWeight: 'bold' }}>
            Project Total
          </Typography>
          <Chip
            label={formatKpiScore(projectTotalKpi)}
            color={getKpiColor(projectTotalKpi)}
            title={`Project Total KPI: ${projectTotalKpi > 0 ? '+' : ''}${projectTotalKpi} business days`}
            sx={{ 
              fontSize: '1rem', 
              fontWeight: 'bold',
              minWidth: 100,
              mb: 1
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {projectTotalUsers} user{projectTotalUsers !== 1 ? 's' : ''} • {projectTotalCompletedTasks} task{projectTotalCompletedTasks !== 1 ? 's' : ''} completed
          </Typography>
        </CardContent>
      </Card>

      {/* Individual Organization Cards */}
      {organizations.map((org) => (
        <Card key={org.type} sx={{ minWidth: 200, flex: '1 1 auto' }}>
          <CardContent>
            <Typography variant="h6" component="div" gutterBottom>
              {org.type}
            </Typography>
            <Chip
              label={formatKpiScore(org.kpiScore)}
              color={getKpiColor(org.kpiScore)}
              title={`KPI Score: ${org.kpiScore > 0 ? '+' : ''}${org.kpiScore} business days`}
              sx={{ 
                fontSize: '1rem', 
                fontWeight: 'bold',
                minWidth: 100,
                mb: 1
              }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {org.userCount} user{org.userCount !== 1 ? 's' : ''} • {org.completedTaskCount} task{org.completedTaskCount !== 1 ? 's' : ''} completed
            </Typography>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

